import { Box, keyframes } from "@mui/material";
import { type SyntheticEvent } from "react";

import { REBRAND_PRIMARY_COLOR } from "@src/appV2/lib/Theme/rebrandConstants";

import superNurseImage from "./SuperNurse.webp";

// Match cbh-mobile-app's design-system LoadingSpinner at size="medium",
// variant="primary": 40px, 0.75s linear rotate, conic-gradient ring with a
// leading head dot. Uses white for contrast on the brand-red background.
const SPINNER_SIZE_PX = 40;
const SPINNER_COLOR = "#FFFFFF";
const SPINNER_CENTER = SPINNER_SIZE_PX * 0.5;
const SPINNER_OUTER_R = SPINNER_SIZE_PX * 0.4;
const SPINNER_INNER_R = SPINNER_SIZE_PX * 0.3;
const SPINNER_HEAD_R = SPINNER_SIZE_PX * 0.05;
const SPINNER_HEAD_CX = SPINNER_SIZE_PX * 0.85;
const SPINNER_HEAD_CY = SPINNER_SIZE_PX * 0.5;

// Match the iOS launch storyboard exactly (mobile/ios/App/App/Base.lproj/
// LaunchScreen.storyboard): image is 60% of viewport width, asset's
// natural aspect ratio 575:913. Both layers must update together;
// if you change these, also update the storyboard constraints.
//
// 60% (down from an initial 75%) keeps the new asset's on-screen size
// roughly aligned with what the previous Lottie ladder rendered at
// 75vw — the new asset's aspect ratio is taller (575:913 vs 216:303),
// so the same width percentage was making it noticeably bigger than
// the native splash.
const IMAGE_WIDTH_VW = 60;
const IMAGE_ASPECT_RATIO = "575 / 913";

// The nurse figure's visual mass sits ~5% to the right of the image's
// geometric centre (the cape extends far left, pulling the bounding box
// off-axis). Shifting the image left by 5% of its own width places the
// person's perceived centre on the screen centre. The iOS storyboard
// applies the equivalent offset via a centerX multiplier of 0.94. If you
// change this constant or IMAGE_WIDTH_VW, recalculate the iOS multiplier:
// multiplier = 1 - (IMAGE_CENTER_OFFSET_PCT × IMAGE_WIDTH_VW / 5000).
const IMAGE_CENTER_OFFSET_PCT = 5;

const rotateKeyframes = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const spinnerGradient = `conic-gradient(from 90deg at 50% 50%, ${SPINNER_COLOR}00 0deg, ${SPINNER_COLOR}0A 0.04deg, ${SPINNER_COLOR}FF 360deg)`;
const spinnerMaskUrl = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${SPINNER_SIZE_PX} ${SPINNER_SIZE_PX}' width='${SPINNER_SIZE_PX}' height='${SPINNER_SIZE_PX}'><mask id='ringMask'><rect x='0' y='0' width='${SPINNER_SIZE_PX}' height='${SPINNER_SIZE_PX}' fill='black' /><circle cx='${SPINNER_CENTER}' cy='${SPINNER_CENTER}' r='${SPINNER_OUTER_R}' fill='white' /><circle cx='${SPINNER_CENTER}' cy='${SPINNER_CENTER}' r='${SPINNER_INNER_R}' fill='black' /></mask><rect x='0' y='0' width='${SPINNER_SIZE_PX}' height='${SPINNER_SIZE_PX}' fill='black' mask='url(%23ringMask)' /></svg>")`;

interface SplashContentProps {
  /**
   * Optional handlers used by the native-splash overlay path (so the
   * Capacitor splash can be dismissed once the JS image has actually
   * decoded and painted). Not needed when used as a Suspense fallback.
   */
  onImageLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onImageError?: () => void;
}

/**
 * The visual portion of the splash: brand-red full-screen, super-nurse image,
 * spinner below. Used both inside MobileSplashOverlay (cold-launch native
 * splash overlay) and as the top-level Suspense fallback in src/index.tsx so
 * the visual is identical when the overlay fades out and reveals whatever
 * lazy chunks are still loading underneath.
 */
export function SplashContent({ onImageLoad, onImageError }: SplashContentProps = {}) {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: REBRAND_PRIMARY_COLOR,
      }}
    >
      {/* Image: visually centered on the nurse figure, dimensions match the iOS launch storyboard. */}
      <Box
        component="img"
        src={superNurseImage}
        alt="Clipboard Health"
        onLoad={onImageLoad}
        onError={onImageError}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(calc(-50% - ${IMAGE_CENTER_OFFSET_PCT}%), -50%)`,
          width: `${IMAGE_WIDTH_VW}vw`,
          aspectRatio: IMAGE_ASPECT_RATIO,
          height: "auto",
          display: "block",
        }}
      />
      {/* Spinner: centered at screen center. After the image offset fix,
          the nurse's visual axis lands on screen center too, so both are
          aligned. The outer Box owns position+centering; the inner Box owns
          the rotate animation — keeping them on different elements so the
          animation's transform doesn't clobber the centering transform. */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: "12vh",
          transform: "translateX(-50%)",
          width: SPINNER_SIZE_PX,
          height: SPINNER_SIZE_PX,
        }}
      >
        <Box
          role="progressbar"
          sx={{
            flexShrink: 0,
            width: SPINNER_SIZE_PX,
            height: SPINNER_SIZE_PX,
            borderRadius: "50%",
            background: spinnerGradient,
            maskImage: spinnerMaskUrl,
            animation: `${rotateKeyframes} 0.75s linear infinite`,
            "& > svg > circle": { fill: SPINNER_COLOR },
          }}
        >
          <svg
            viewBox={`0 0 ${SPINNER_SIZE_PX} ${SPINNER_SIZE_PX}`}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx={SPINNER_HEAD_CX} cy={SPINNER_HEAD_CY} r={SPINNER_HEAD_R} />
          </svg>
        </Box>
      </Box>
    </Box>
  );
}
